<template>
  <section class="section content-container">
    <div class="container is-fluid">
      <div class="columns">
        <div class="column">
          <TitledSection class="container island is-fluid">
            <template v-slot:icon>
              <font-awesome-icon icon="cubes" />
            </template>
            <template v-slot:title>Public Apps</template>
            <template v-slot:subtitle> Apps that have been installed for this workspace.</template>
            <div class="columns island-content">
              <div class="column">
                <n-loading-spinner v-if="!publishedAppsLoaded" style="margin: 4rem auto" />

                <div v-else-if="publishedApps.length === 0" class="no-apps">
                  <NoDataIcon class="no-apps-icon" />
                  <div class="no-apps-text">No public apps installed</div>
                </div>

                <table v-else class="table is-fullwidth">
                  <thead>
                    <tr>
                      <th style="width: 40px">&nbsp;</th>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="app in publishedApps" :key="app.id">
                      <td>
                        <svg class="app-icon channel-icon-svg">
                          <use
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            :xlink:href="`#${app.icon === 'webassistant' ? 'web-assistant' : app.icon}`"
                          ></use>
                        </svg>
                      </td>
                      <td>{{ app.name }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </TitledSection>
        </div>
      </div>
    </div>
  </section>

  <section class="section content-container">
    <div class="container is-fluid">
      <div class="columns">
        <div class="column">
          <TitledSection class="container island is-fluid">
            <template v-slot:icon>
              <font-awesome-icon icon="code" />
            </template>
            <template v-slot:title>Private Apps</template>
            <template v-slot:subtitle> Custom apps configured in there workspaces. </template>
            <div class="columns island-content">
              <n-loading-spinner v-if="!privateAppsLoaded" style="margin: 4rem auto" />

              <div v-else-if="privateApps.length === 0" class="no-apps">
                <NoDataIcon class="no-apps-icon" />
                <div class="no-apps-text">No private apps configured</div>
              </div>

              <table v-else class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Supports bot action</th>
                    <th>Supports homescreen</th>
                    <th>Supports webhooks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="app in privateApps" :key="app.id">
                    <td>{{ app.name }}</td>
                    <td>
                      <n-tooltip wide>
                        <span class="tag" :class="getStatusClass(app.capabilities?.supportsHomescreen)">
                          {{ getStatus(app.capabilities?.supportsHomescreen) }}
                        </span>
                        <template v-if="hasActionUrls(app)" #message>
                          <table>
                            <tr v-if="app.workflowOptions.configUrl">
                              <td><b>Config Url:</b></td>
                              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
                              <td>{{ app.workflowOptions.configUrl }}</td>
                            </tr>
                            <tr v-if="app.workflowOptions.initUrl">
                              <td><b>Init Url:</b></td>
                              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
                              <td>{{ app.workflowOptions.initUrl }}</td>
                            </tr>
                            <tr v-if="app.workflowOptions.submitUrl">
                              <td><b>Submit Url:</b></td>
                              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
                              <td>{{ app.workflowOptions.submitUrl }}</td>
                            </tr>
                          </table>
                        </template>
                      </n-tooltip>
                    </td>
                    <td>
                      <n-tooltip wide>
                        <span class="tag" :class="getStatusClass(app.capabilities?.supportsBotAction)">
                          {{ getStatus(app.capabilities?.supportsBotAction) }}
                        </span>
                        <template v-if="hasActionUrls(app)" #message>
                          <table>
                            <tr v-if="app.workflowOptions.configUrl">
                              <td><b>Config Url:</b></td>
                              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
                              <td>{{ app.workflowOptions.configUrl }}</td>
                            </tr>
                            <tr v-if="app.workflowOptions.initUrl">
                              <td><b>Init Url:</b></td>
                              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
                              <td>{{ app.workflowOptions.initUrl }}</td>
                            </tr>
                            <tr v-if="app.workflowOptions.submitUrl">
                              <td><b>Submit Url:</b></td>
                              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
                              <td>{{ app.workflowOptions.submitUrl }}</td>
                            </tr>
                          </table>
                        </template>
                      </n-tooltip>
                    </td>
                    <td>
                      <n-tooltip wide>
                        <span class="tag" :class="getStatusClass(app.capabilities?.supportsWebhooks)">
                          {{ getStatus(app.capabilities?.supportsWebhooks) }}
                        </span>
                        <template v-if="app.workflowOptions?.webhookUrl" #message>
                          <table>
                            <tr>
                              <td><b>Webhook Url:</b></td>
                              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
                              <td>{{ app.workflowOptions.webhookUrl }}</td>
                            </tr>
                          </table>
                        </template>
                      </n-tooltip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </TitledSection>
        </div>
      </div>
    </div>
  </section>

  <section class="section content-container">
    <div class="container is-fluid">
      <div class="columns">
        <div class="column">
          <TitledSection class="container island is-fluid">
            <template v-slot:icon>
              <font-awesome-icon icon="cog" />
            </template>
            <template v-slot:title>Features</template>
            <template v-slot:subtitle> Licensed features currently set and editable. </template>
            <div class="columns island-content">
              <n-loading-spinner v-if="!featuresLoaded" style="margin: 4rem auto" />
              <template v-else>
                <div class="feature-selector__list-item" v-for="feature in featureFlags" :key="feature.id">
                  <n-toggle
                    :id="`toggle-${feature.id}`"
                    v-model="workspace.features[feature.id]"
                    :label="feature.displayName"
                  ></n-toggle>
                </div>
                <div class="save-bot column">
                  <n-button @click="updateFeatures()" variant="primary" class="save-button" :loading="updateInProgress">
                    Update
                  </n-button>
                </div>
              </template>
            </div>
          </TitledSection>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import TitledSection from '@shared/components/TitledSection.vue';
import { computed, onMounted, ref } from 'vue';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { useRoute } from 'vue-router';
import FeatureFlagViewModel from '@/workspaces/models/featureFlagViewModel';
import { workspacesApi } from '@/workspaces/workspaces';
import Toast from '@shared/services/toast';
import { appsApi } from '@/features/apps/shared/services/apps';
import { PrivateApp, PublishedApp } from '@/features/apps/shared/models';
import NoDataIcon from '@/shared/icons/InsertChartOutlined.vue';

const route = useRoute();
const workspaceId = computed(() => <string>route.params.id);
const publishedApps = ref<PublishedApp[]>();
const privateApps = ref<PrivateApp[]>();
const workspace = ref<WorkspaceViewModel>();
const featureFlags = ref<FeatureFlagViewModel[]>([]);
const updateInProgress = ref(false);

const featuresLoaded = computed(() => !!workspace.value && featureFlags.value.length > 0);
const publishedAppsLoaded = computed(() => publishedApps.value !== undefined);
const privateAppsLoaded = computed(() => privateApps.value !== undefined);

onMounted(async () => {
  publishedApps.value = await appsApi.listPublished(workspaceId.value);
  publishedApps.value = publishedApps.value.filter((a: PublishedApp) => a.isInstalled);
  privateApps.value = await appsApi.listPrivate(workspaceId.value);
  workspace.value = await workspacesApi.getById(workspaceId.value);
  featureFlags.value = await workspacesApi.getFeatureFlags();

  workspace.value.features ??= [];
  for (const feature of featureFlags.value) {
    if (workspace.value.features[feature.id] === undefined) {
      workspace.value.features[feature.id] = feature.defaultValue;
    }
  }
});

function getStatus(capabilityEnabled: boolean): string {
  return capabilityEnabled ? 'Enabled' : 'Disabled';
}

function getStatusClass(capabilityEnabled: boolean): string {
  return capabilityEnabled ? 'is-success' : 'is-danger';
}

function hasActionUrls(app: PrivateApp) {
  return !!app.workflowOptions?.configUrl || !!app.workflowOptions?.initUrl || !!app.workflowOptions?.submitUrl;
}

function getAppActionUrls(app: PrivateApp) {
  const values: string[] = [];
  if (app.workflowOptions?.configUrl) {
    values.push(`Config Url: ${app.workflowOptions.configUrl}`);
  }
  if (app.workflowOptions?.initUrl) {
    values.push(`Init Url: ${app.workflowOptions.initUrl}`);
  }
  if (app.workflowOptions?.submitUrl) {
    values.push(`Submit Url: ${app.workflowOptions.submitUrl}`);
  }
  return values.join('\n');
}

async function updateFeatures() {
  updateInProgress.value = true;
  try {
    workspace.value = await workspacesApi.update(workspaceId.value, workspace.value);
    Toast.showSuccess('Updated workspace features.');
  } catch (err: any) {
    Toast.showError(`Failed to update features. ${err?.message || err}`);
  } finally {
    updateInProgress.value = false;
  }
}
</script>

<style scoped lang="scss">
.island {
  margin-bottom: 1.5em;
  min-height: 220px;
}

.island-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  .column {
    width: 100%;
    padding: 0.75rem;
  }

  .field {
    display: flex;
    align-items: flex-start;
  }

  .contact-information {
    display: flex;
    margin-bottom: 15px;
    line-height: 24px;
    align-items: center;

    &:last-of-type {
      margin-bottom: 40px;
    }

    .topic-tags {
      width: calc(100% - 10em);
      padding: 0;
    }

    .contact-attribute-label {
      width: 12em;
    }

    label {
      height: 1.5em;
    }

    p {
      margin-left: 1em;
    }
  }
}

.header {
  padding-top: 3em;
}

.channel-icon-svg {
  width: 32px;
  height: 32px;
  vertical-align: middle;
  margin-right: 16px;
}

.feature-selector {
  &__list-item {
    margin: 0.5rem 1rem;
    display: flex;
    align-items: center;
    .icon {
      margin-right: 0.5rem;
      cursor: move;
      align-self: center;
      position: relative;
      height: 1em;
      width: 1em;
      left: -6px;
    }
  }
}

.no-apps {
  color: $color-netcall-body-text;
  opacity: 0.75;
  position: absolute;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  justify-content: center;
  align-items: center;

  .no-apps-text {
    text-align: center;
  }
}

.no-apps-icon {
  display: block;
  margin: 1rem auto;
  font-size: 2em;
}
</style>
